<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Auth from 'dashboard/api/auth';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import wootConstants from 'dashboard/constants/globals';
import Avatar from 'next/avatar/Avatar.vue';
import SidebarProfileMenuStatus from './SidebarProfileMenuStatus.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSeparator,
  DropdownItem,
} from 'next/dropdown-menu/base';
import CustomBrandPolicyWrapper from '../../components/CustomBrandPolicyWrapper.vue';
import NewTicketDialog from 'dashboard/components-next/tickets/NewTicketDialog.vue';
import TicketsDialog from 'dashboard/components-next/tickets/TicketsDialog.vue';
import { useTicketsStore } from 'dashboard/stores/tickets';
import { useReleaseNotesStore } from 'dashboard/stores/releaseNotes';

defineProps({
  isCollapsed: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'openKeyShortcutModal']);

defineOptions({
  inheritAttrs: false,
});

const { t } = useI18n();

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const accountId = useMapGetter('getCurrentAccountId');
const globalConfig = useMapGetter('globalConfig/get');
const releaseNotesStore = useReleaseNotesStore();
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const route = useRoute();
const router = useRouter();
const ticketsStore = useTicketsStore();
const newTicketDialog = ref(null);
const ticketsDialog = ref(null);

const usesInternalTickets = computed(
  () => globalConfig.value.ticketsProvider === 'internal'
);

function openNewTicket() {
  if (usesInternalTickets.value) {
    newTicketDialog.value.open();
    return;
  }
  const url = `${wootConstants.JIRA_URL}portal/3/group/4/create/10073?customfield_10063=${accountId.value}&customfield_10064=${currentUser.value.email}&customfield_10066=${globalConfig.value.appVersion}&customfield_10065=${globalConfig.value.maVersion}`;
  window.open(url, '_blank');
}

function openMyTickets() {
  if (usesInternalTickets.value) {
    ticketsDialog.value.open();
    return;
  }
  const url = `${wootConstants.JIRA_URL}user/requests?filter=MASUP`;
  window.open(url, '_blank');
}

const showChatSupport = computed(() => {
  return (
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CONTACT_CHATWOOT_SUPPORT_TEAM
    ) && globalConfig.value.chatwootInboxToken
  );
});

const toggleChatSupport = () => {
  if (window.$chatwoot) {
    window.$chatwoot.toggle();
  }
};

const menuItems = computed(() => {
  return [
    {
      show: showChatSupport.value,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CONTACT_SUPPORT'),
      icon: 'i-lucide-life-buoy',
      click: toggleChatSupport,
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.KEYBOARD_SHORTCUTS'),
      icon: 'i-lucide-keyboard',
      click: () => {
        emit('openKeyShortcutModal');
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.RELEASE_NOTES'),
      icon: 'i-lucide-sparkles',
      click: () => releaseNotesStore.openHistory(),
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.PROFILE_SETTINGS'),
      icon: 'i-lucide-user-pen',
      link: { name: 'profile_settings_index' },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.APPEARANCE'),
      icon: 'i-lucide-palette',
      click: () => {
        const ninja = document.querySelector('ninja-keys');
        ninja.open({ parent: 'appearance_settings' });
      },
    },
    // {
    //   show: true,
    //   showOnCustomBrandedInstance: false,
    //   label: t('SIDEBAR_ITEMS.DOCS'),
    //   icon: 'i-lucide-book',
    //   link: 'https://www.chatwoot.com/hc/user-guide/en',
    //   nativeLink: true,
    //   target: '_blank',
    // },
    {
      show: currentUser.value.type === 'SuperAdmin',
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.SUPER_ADMIN_CONSOLE'),
      icon: 'i-lucide-castle',
      link: '/super_admin',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.NEW_TICKET'),
      icon: 'i-lucide-ticket-plus',
      click: openNewTicket,
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.OPEN_TICKETS'),
      icon: 'i-lucide-ticket-check',
      click: openMyTickets,
      hasUnread: usesInternalTickets.value && ticketsStore.unreadCount > 0,
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.LOGOUT'),
      icon: 'i-lucide-power',
      click: Auth.logout,
    },
  ];
});

// Os e-mails de chamado apontam para o painel com ?ticket=ID para abrir direto nele.
onMounted(() => {
  if (!usesInternalTickets.value) return;
  ticketsStore.fetchSummary();
  const ticketId = Number(route.query.ticket);
  if (!ticketId) return;
  ticketsDialog.value.open(ticketId);
  router.replace({ query: { ...route.query, ticket: undefined } });
});

const allowedMenuItems = computed(() => {
  return menuItems.value.filter(item => item.show);
});
</script>

<template>
  <DropdownContainer
    class="relative min-w-0"
    :class="isCollapsed ? 'w-auto' : 'w-full'"
    @close="emit('close')"
  >
    <template #trigger="{ toggle, isOpen }">
      <button
        class="flex gap-2 items-center p-1 text-left rounded-lg cursor-pointer hover:bg-n-alpha-1"
        :class="[
          { 'bg-n-alpha-1': isOpen },
          isCollapsed ? 'justify-center' : 'w-full',
        ]"
        :title="isCollapsed ? currentUser.available_name : undefined"
        @click="toggle"
      >
        <span class="relative flex-shrink-0">
          <Avatar
            :size="32"
            :name="currentUser.available_name"
            :src="currentUser.avatar_url"
            :status="currentUserAvailability"
          />
          <span
            v-if="usesInternalTickets && ticketsStore.unreadCount"
            class="absolute top-0 rounded-full size-2.5 bg-n-blue-9 ltr:right-0 rtl:left-0 outline outline-2 outline-n-solid-1"
          />
        </span>
        <div v-if="!isCollapsed" class="min-w-0">
          <div class="text-sm font-medium leading-4 truncate text-n-slate-12">
            {{ currentUser.available_name }}
          </div>
          <div class="text-xs truncate text-n-slate-11">
            {{ currentUser.email }}
          </div>
        </div>
      </button>
    </template>
    <DropdownBody class="ltr:left-0 rtl:right-0 bottom-12 z-50 w-81 mb-2">
      <SidebarProfileMenuStatus />
      <DropdownSeparator />
      <template v-for="item in allowedMenuItems" :key="item.label">
        <CustomBrandPolicyWrapper
          :show-on-custom-branded-instance="item.showOnCustomBrandedInstance"
        >
          <DropdownItem v-if="item.show" v-bind="item">
            <template v-if="item.hasUnread" #label>
              <span class="flex items-center justify-between flex-1 gap-2">
                {{ item.label }}
                <span class="rounded-full size-2 bg-n-blue-9" />
              </span>
            </template>
          </DropdownItem>
        </CustomBrandPolicyWrapper>
      </template>
    </DropdownBody>
  </DropdownContainer>
  <NewTicketDialog
    v-if="usesInternalTickets"
    ref="newTicketDialog"
    @created="ticket => ticketsDialog.open(ticket.id)"
  />
  <TicketsDialog
    v-if="usesInternalTickets"
    ref="ticketsDialog"
    @new-ticket="newTicketDialog.open()"
  />
</template>
